/**
 * MKS-137 — "Contract & billing" on the project detail page.
 *
 * Everything about how this project is billed, in one read-only place. Until
 * now a PM had to open the edit form to see any of it: the billing terms
 * (MKS-94) and the billing schedule (MKS-136) both live on ProjectFormPage, and
 * `TypePill` was only ever rendered on the project list.
 *
 * Read-only by design — every value here has an editor already, and the "Edit"
 * link points at it. `canEdit` (admin, finance, or this project's PM) decides
 * only whether that link is on screen; it is an affordance, not a gate.
 *
 * `canOpenInvoice` is a SEPARATE, narrower permission on purpose: `/invoices/:id`
 * is behind `<FinanceRoute>`, so a PM following an invoice link would land on
 * `/unauthorized`. They see the "Invoiced" status without the link — the same
 * lesson the "Manage SLA priority tiers" link on this page records (WR-01).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { TypePill } from '../ui/TypePill';
import type { ProjectTypeTone } from '../ui/TypePill';
import { projectTypeTone } from '../../pages/projects/projectTypeTone';
import {
  FIXED_PRICE_PAYMENT_TYPE_ID,
  SLA_BALICEK_PAYMENT_TYPE_ID,
} from '../../constants/typeIds';
import { useBillingSchedule } from '../../hooks/useBillingPeriods';
import {
  BILLING_PERIOD_STATUS_CLASSES,
  BILLING_PERIOD_STATUS_LABELS,
} from '../../services/api/billingPeriods';
import type { BillingPeriod } from '../../services/api/billingPeriods';
import { BILLING_FREQUENCY_LABELS } from '../../services/api/leads';
import type { Project } from '../../services/api/projects';
import { formatCurrency, formatDisplayDate } from '../../utils/format';

/** Em dash — every missing value reads the same way, and none of them throws. */
const DASH = '—';

interface ContractBillingSectionProps {
  project: Project;
  /** Admin, finance, or this project's PM — shows the Edit link. */
  canEdit: boolean;
  /** Admin or finance — `/invoices/:id` is behind `<FinanceRoute>`. */
  canOpenInvoice: boolean;
}

/**
 * The billing type's pill tone. The LABEL comes from the backend-resolved
 * `paymentType.name` (the PaymentType catalogue route is admin-only, so the
 * page cannot look it up itself) — this only picks the colour, the same three
 * tones `projectTypeTone` maps to.
 */
function paymentTypeTone(paymentTypeId: number | null | undefined): ProjectTypeTone {
  if (paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID) return 'sla';
  if (paymentTypeId == null) return 'default';
  return 'mixed';
}

/** UTC today as YYYY-MM-DD, so it compares lexicographically with the API's dates. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Which period is next, and which ones are late.
 *
 * `overdue` is deliberately only `planned` periods past their planned end — a
 * `ready_to_invoice` period past its end date is waiting on finance, not on the
 * PM, and flagging every one of those would make the highlight meaningless.
 * `next` is the earliest still-planned period that is NOT already overdue.
 */
function resolveHighlights(periods: BillingPeriod[]): {
  nextId: number | null;
  overdueIds: Set<number>;
} {
  const today = todayIso();
  const overdueIds = new Set<number>();
  let next: BillingPeriod | null = null;

  for (const period of periods) {
    if (period.status !== 'planned') continue;
    if (period.plannedEndDate < today) {
      overdueIds.add(period.id);
      continue;
    }
    if (
      next === null ||
      period.plannedEndDate < next.plannedEndDate ||
      (period.plannedEndDate === next.plannedEndDate && period.sequence < next.sequence)
    ) {
      next = period;
    }
  }

  return { nextId: next?.id ?? null, overdueIds };
}

/** A string decimal from the API, or null when it is absent or unparseable. */
function toNumber(value: string | null | undefined): number | null {
  if (value == null) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

export function ContractBillingSection({
  project,
  canEdit,
  canOpenInvoice,
}: ContractBillingSectionProps) {
  const { data: scheduleData } = useBillingSchedule(project.id);
  const schedule = scheduleData?.data;
  const periods = schedule?.periods ?? [];
  const { nextId, overdueIds } = resolveHighlights(periods);

  const projectType = projectTypeTone(project.projectTypeId);
  const contractAmount = toNumber(project.contractAmount);
  const invoiced = toNumber(project.contractInvoiced);
  const remaining = toNumber(project.contractRemaining);
  // Mirrors the backend's own classifier (`contractAmount > 0 → fixed_price`),
  // so a project carrying a contract amount shows its burn-down even when the
  // payment template was never switched over.
  const isFixedPrice =
    project.paymentTypeId === FIXED_PRICE_PAYMENT_TYPE_ID || contractAmount != null;
  const contractCurrency = project.contractCurrency ?? 'CZK';
  const scheduleCurrency = schedule?.contractCurrency ?? contractCurrency;

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 p-6 space-y-5"
      data-testid="contract-billing-section"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Contract &amp; billing</h2>
        {canEdit && (
          <Link
            to={`/projects/${project.id}/edit`}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Edit &rarr;
          </Link>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
        <Field label="Billing type">
          {project.paymentType?.name ? (
            <TypePill tone={paymentTypeTone(project.paymentTypeId)} label={project.paymentType.name} />
          ) : (
            DASH
          )}
        </Field>
        <Field label="Project type">
          <TypePill tone={projectType.tone} label={projectType.label} />
        </Field>
        <Field label="Invoice due days">
          {project.paymentTerm
            ? `${project.paymentTerm.name} (${project.paymentTerm.days} days)`
            : DASH}
        </Field>
        <Field label="Billing frequency">
          {project.contractFrequency
            ? (BILLING_FREQUENCY_LABELS[project.contractFrequency] ?? project.contractFrequency)
            : DASH}
        </Field>
        <Field label="Contract start">
          {project.contractStart ? formatDisplayDate(project.contractStart) : DASH}
        </Field>
        <Field label="Contract end">
          {project.contractEnd ? formatDisplayDate(project.contractEnd) : DASH}
        </Field>
        <div className="sm:col-span-2">
          <dt className="text-sm font-medium text-gray-500">Contract</dt>
          <dd className="mt-0.5 text-sm text-gray-900">
            {project.contractUrl ? (
              <a
                href={project.contractUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline break-all"
              >
                {project.contractUrl}
              </a>
            ) : (
              DASH
            )}
          </dd>
        </div>
      </dl>

      {isFixedPrice && (
        <dl
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-lg bg-gray-50 px-4 py-3"
          data-testid="fixed-price-burndown"
        >
          <Field label="Contract amount">
            {contractAmount == null ? DASH : formatCurrency(contractAmount, false, contractCurrency)}
          </Field>
          <Field label="Invoiced so far">
            {invoiced == null ? DASH : formatCurrency(invoiced, false, contractCurrency)}
          </Field>
          <Field label="Remaining">
            {remaining == null ? DASH : formatCurrency(remaining, false, contractCurrency)}
          </Field>
        </dl>
      )}

      {schedule && (
        <div data-testid="billing-schedule-readonly">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Billing schedule</h3>
          {periods.length === 0 ? (
            <p className="text-sm text-gray-500">No billing periods yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-3 font-medium">#</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Billed range</th>
                    <th className="py-2 pr-3 font-medium text-right">Amount</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 font-medium">Invoice</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {periods.map((period) => {
                    const isNext = period.id === nextId;
                    const isOverdue = overdueIds.has(period.id);
                    return (
                      <tr
                        key={period.id}
                        data-testid={`contract-billing-period-${period.id}`}
                        className={
                          isOverdue ? 'bg-red-50' : isNext ? 'bg-primary-50' : undefined
                        }
                      >
                        <td className="py-2 pr-3 text-gray-500">{period.sequence}</td>
                        <td className="py-2 pr-3 text-gray-900">
                          {period.name}
                          {isNext && (
                            <span className="ml-2 rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-800">
                              Next
                            </span>
                          )}
                          {isOverdue && (
                            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-800">
                              Past planned date
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                          {formatDisplayDate(period.billedFrom)} &ndash;{' '}
                          {formatDisplayDate(period.billedTo)}
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          {period.plannedAmount === null
                            ? DASH
                            : formatCurrency(
                                period.plannedAmount,
                                false,
                                period.currency ?? scheduleCurrency,
                              )}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs ${BILLING_PERIOD_STATUS_CLASSES[period.status]}`}
                          >
                            {BILLING_PERIOD_STATUS_LABELS[period.status]}
                          </span>
                        </td>
                        <td className="py-2 whitespace-nowrap">
                          {period.invoiceId == null ? (
                            <span className="text-gray-400">{DASH}</span>
                          ) : canOpenInvoice ? (
                            <Link
                              to={`/invoices/${period.invoiceId}`}
                              className="text-primary-600 hover:underline"
                            >
                              #{period.invoiceId}
                            </Link>
                          ) : (
                            <span className="text-gray-600">#{period.invoiceId}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
